import axios from "axios";
import { globalErrorHandler } from "../utils/ErrorHandler";
import { encryptMsalValue, decryptMsalValue } from "../utils/msalStorage";

// Base API URLs
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AGENT_BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL;
const CSRF_TOKEN_ENDPOINT = "/api/Auth/csrf-token";

// CSRF Token Management with Expiration Handling
class CSRFTokenManager {
  constructor() {
    this.token = null;
    this.tokenExpiryTime = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    
    // Backend CSRF token lifetime: 15 minutes
    // Use 14 minutes (840000ms) to refresh before actual expiry
    this.TOKEN_LIFETIME = 10 * 60 * 1000;
  }

  /**
   * Get CSRF token - fetches new one if expired or doesn't exist
   * @param {Object} authHeaders - Authorization headers for CSRF fetch
   * @returns {Promise<string>} CSRF token
   */
  async getToken(authHeaders) {
    // Check if token exists and is still valid
    if (this.token && this.isTokenValid()) {
      console.log("✅ Using cached CSRF token");
      return this.token;
    }

    // Token expired or doesn't exist
    if (this.token && !this.isTokenValid()) {
      console.log("⏰ CSRF token expired - fetching new token");
      this.clearToken();
    }

    // If already refreshing, wait for that promise
    if (this.isRefreshing) {
      console.log("⏳ CSRF token refresh already in progress - waiting...");
      return this.refreshPromise;
    }

    return this.refreshToken(authHeaders);
  }

  /**
   * Check if current token is still valid
   * @returns {boolean} True if token is valid
   */
  isTokenValid() {
    if (!this.tokenExpiryTime) {
      return false;
    }

    const now = Date.now();
    const isValid = now < this.tokenExpiryTime;

    if (!isValid) {
      console.warn("⚠️ CSRF token has expired");
    }

    return isValid;
  }

  /**
   * Fetch new CSRF token from backend
   * @param {Object} authHeaders - Authorization headers
   * @returns {Promise<string>} New CSRF token
   */
  async refreshToken(authHeaders) {
    this.isRefreshing = true;

    this.refreshPromise = axios
      .get(`${BASE_URL}${CSRF_TOKEN_ENDPOINT}`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      })
      .then((response) => {
        this.token = response.data.token || response.data.csrfToken;
        this.tokenExpiryTime = Date.now() + this.TOKEN_LIFETIME;
        
        const expiryDate = new Date(this.tokenExpiryTime).toLocaleTimeString();
        console.log(`✅ CSRF Token fetched successfully (expires at: ${expiryDate})`);
        
        this.isRefreshing = false;
        this.refreshPromise = null;
        return this.token;
      })
      .catch((error) => {
        this.isRefreshing = false;
        this.refreshPromise = null;
        console.error("❌ CSRF token fetch failed:", error.message);
        throw error;
      });

    return this.refreshPromise;
  }

  /**
   * Clear stored CSRF token
   */
  clearToken() {
    console.log("🗑️ Clearing CSRF token");
    this.token = null;
    this.tokenExpiryTime = null;
  }

  /**
   * Get remaining token lifetime in minutes
   * @returns {number} Minutes until token expires
   */
  getRemainingLifetime() {
    if (!this.tokenExpiryTime) {
      return 0;
    }

    const remaining = this.tokenExpiryTime - Date.now();
    return Math.max(0, Math.floor(remaining / 60000));
  }
}

const csrfManager = new CSRFTokenManager();

// Auto-logout timer reset function (set from Layout component)
let globalResetTimer = null;

/**
 * Set the auto-logout reset function to be called on API requests
 * @param {Function} resetFn - Function to reset the auto-logout timer
 */
export const setAutoLogoutReset = (resetFn) => {
  globalResetTimer = resetFn;
};

const safeJsonParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to parse MSAL storage entry:", error);
    return null;
  }
};

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
  try {
    // Get all keys from sessionStorage
    const keys = Object.keys(sessionStorage);
    
    // Look for msal token
    const mslKeys = keys.filter(k => k.includes("msal") && k.includes("access_token"));
    
    if (mslKeys.length > 0) {
      const tokenData = decryptMsalValue(sessionStorage.getItem(mslKeys[0]));
      if (tokenData) {
        const parsed = safeJsonParse(tokenData);
        return parsed?.secret || parsed?.access_token || null;
      }
    }

    // Fallback: try the original method
    const idTokenKey = decryptMsalValue(
      sessionStorage.getItem(
        `msal.token.keys.${process.env.REACT_APP_MSAL_CLIENT_ID}`
      )
    );

    if (!idTokenKey) {
      console.warn("⚠️ No MSAL token key found");
      return null;
    }

    const parsedIdTokenKey = safeJsonParse(idTokenKey);
    if (!parsedIdTokenKey?.idToken?.length) {
      console.warn("⚠️ Malformed MSAL token key payload");
      return null;
    }

    const token = decryptMsalValue(
      sessionStorage.getItem(`${parsedIdTokenKey.idToken[0]}`)
    );

    if (!token) {
      console.warn("⚠️ No token found in sessionStorage");
      return null;
    }

    const parsedToken = safeJsonParse(token);
    const tokenAuth = encryptMsalValue(parsedToken?.secret || null);
    return tokenAuth;
  } catch (error) {
    console.error("❌ Error retrieving auth token:", error);
    return null;
  }
};

// Create API instances
const axiosClient = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Handle unauthorized response
const handleUnauthorized = () => {
  console.warn("🚨 Unauthorized - Clearing session and redirecting");
  csrfManager.clearToken();
  globalErrorHandler.handleUnauthorized();
};

// Request interceptor - FIRST: Add Auth Token
const addAuthTokenInterceptor = (client) => {
  client.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      
      if (token) {
        config.headers.Authorization = `${token}`;
        console.log(`✅ Auth header added for ${config.method.toUpperCase()} ${config.url}`);
      } else {
        console.warn(`⚠️ No auth token available for ${config.method.toUpperCase()} ${config.url}`);
      }
      
      // Reset auto-logout timer on API calls (user activity)
      if (globalResetTimer && typeof globalResetTimer === 'function') {
        globalResetTimer();
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// CSRF Token Interceptor - SECOND: Add CSRF token to state-changing requests
const addCSRFTokenInterceptor = (client) => {
  client.interceptors.request.use(
    async (config) => {
      const stateChangingMethods = ["post", "put", "delete", "patch"];

      if (stateChangingMethods.includes(config.method?.toLowerCase())) {
        try {
          const token = getAuthToken();
          
          if (!token) {
            console.error("❌ No auth token available for CSRF fetch");
            return Promise.reject(new Error("No authentication token"));
          }

          // Get CSRF token (will auto-refresh if expired)
          const csrfToken = await csrfManager.getToken({
            Authorization: `${token}`,
          });
          
          config.headers["X-CSRF-Token"] = csrfToken;
          
          const remainingTime = csrfManager.getRemainingLifetime();
          console.log(
            `✅ CSRF header added for ${config.method.toUpperCase()} ${config.url} ` +
            `(token valid for ${remainingTime} more minutes)`
          );
        } catch (error) {
          console.error(`❌ Failed to add CSRF token for ${config.method.toUpperCase()} ${config.url}`);
          return Promise.reject(error);
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Response interceptor - Handle errors with global error handler
const addResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => {
      console.log(`✅ Success: ${response.config.method.toUpperCase()} ${response.config.url}`);
      return response;
    },
    async (error) => {
      const status = error.response?.status;
      const originalRequest = error.config;

      // Use global error handler for safe error logging
      globalErrorHandler.handleError(error, 'API Response');

      if (status === 401) {
        handleUnauthorized();
        return Promise.reject(error);
      }

      // Handle 403 Forbidden - likely invalid/expired CSRF token
      if (status === 403) {
        const shouldClearCSRF = globalErrorHandler.handleForbidden(error);
        
        // Retry logic: Clear CSRF and retry request once
        if (shouldClearCSRF && !originalRequest._csrfRetry) {
          console.warn("⚠️ 403 Forbidden - Possible invalid CSRF token, retrying with fresh token...");
          
          // Mark request to prevent infinite retry loop
          originalRequest._csrfRetry = true;
          
          // Clear expired/invalid token
          csrfManager.clearToken();
          
          try {
            const token = getAuthToken();
            
            if (!token) {
              console.error("❌ No auth token available for CSRF retry");
              return Promise.reject(error);
            }

            // Fetch fresh CSRF token
            const newCsrfToken = await csrfManager.refreshToken({
              Authorization: `${token}`,
            });
            
            // Update request header with new token
            originalRequest.headers["X-CSRF-Token"] = newCsrfToken;
            console.log("🔄 Retrying request with fresh CSRF token");
            
            // Retry the original request
            return client(originalRequest);
          } catch (refreshError) {
            console.error("❌ Failed to refresh CSRF token for retry");
            return Promise.reject(refreshError);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

// Create agent API instance
const agentApiInstance = axios.create({
  baseURL: AGENT_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth interceptor to agent API
addAuthTokenInterceptor(agentApiInstance);

// Simple error handler for agent API
agentApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }

    if (error.response?.status === 404) {
      throw new Error(
        "Query endpoint not found. Please check if the service is running."
      );
    } else if (error.response?.status === 400) {
      throw new Error(
        "Invalid query parameters. Please check your input."
      );
    } else if (error.response?.status >= 500) {
      throw new Error(
        "Server error occurred while processing your query. Please try again later."
      );
    } else if (error.code === "ECONNABORTED") {
      throw new Error(
        "Query request timed out. The service might be experiencing high load."
      );
    } else if (!error.response && error.request) {
      throw new Error(
        "No response received from the query service. Please check your network connection."
      );
    }

    throw error;
  }
);

// Apply interceptors in CORRECT ORDER
addAuthTokenInterceptor(axiosClient);
addCSRFTokenInterceptor(axiosClient);
addResponseInterceptor(axiosClient);
addResponseInterceptor(agentApiInstance);

// Export clients and CSRF manager
export { axiosClient as default, agentApiInstance, csrfManager };